/** Token "vivo" de la sesión (Eduardo 2026-09-18).
 *
 * Problema: la sesión caducaba a las 24 h sin aviso y el guardado en curso
 * fallaba; al recargar se perdía lo escrito. La solución obvia (renovar el
 * token y meterlo al estado de React) es peligrosa: muchas pantallas recargan
 * sus datos cuando cambia `token` y eso PISA el formulario que el admin está
 * llenando — justo lo que se quería evitar.
 *
 * Por eso la renovación es invisible para React: el estado conserva el token
 * con el que arrancó la pestaña y aquí se registra "ese token ahora es este
 * otro". `resolveLiveToken` lo traduce justo antes de cada petición.
 */

const replacements = new Map<string, string>();

export const SESSION_EXPIRED_EVENT = "t123:session-expired";

export function registerTokenReplacement(staleToken: string, freshToken: string): void {
  if (!staleToken || !freshToken || staleToken === freshToken) return;
  replacements.set(staleToken, freshToken);
}

export function clearTokenReplacements(): void {
  replacements.clear();
}

/** Token que de verdad debe viajar en la petición. Sigue la cadena de
 * renovaciones (A→B→C) con tope, por si alguna vez se formara un ciclo. */
export function resolveLiveToken(token: string | null | undefined): string | null {
  if (!token) return null;
  let current = token;
  for (let hops = 0; hops < 50; hops += 1) {
    const next = replacements.get(current);
    if (!next) return current;
    current = next;
  }
  return current;
}

export interface TokenClaims {
  sub?: string;
  exp?: number;
  iat?: number;
  impersonation?: boolean;
}

/** Lee los claims del JWT SIN validar la firma: solo sirve para decidir
 * cuándo renovar; la autoridad sigue siendo el backend. */
export function readTokenClaims(token: string | null | undefined): TokenClaims | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    const claims = JSON.parse(json) as TokenClaims;
    return claims && typeof claims === "object" ? claims : null;
  } catch {
    return null;
  }
}

/** ¿Toca renovar? Cuando queda menos de la cuarta parte de la vida del token
 * (con 24 h: las últimas 6). Nunca para suplantación ni para un token vencido. */
export function shouldRefreshToken(token: string | null | undefined, nowMs: number = Date.now()): boolean {
  const claims = readTokenClaims(token);
  if (!claims || claims.impersonation === true) return false;
  if (typeof claims.exp !== "number") return false;
  const remainingMs = claims.exp * 1000 - nowMs;
  if (remainingMs <= 0) return false;
  const lifetimeMs = typeof claims.iat === "number" ? (claims.exp - claims.iat) * 1000 : 24 * 60 * 60 * 1000;
  return remainingMs < lifetimeMs / 4;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Sesión larga del jugador: el token de renovación se rota (deslizante) al
 * abrir la app si se RECIBIÓ hace más de un día. Así, mientras abra la app al
 * menos una vez cada 90 días, la sesión no vence.
 *
 * Se mide con la hora LOCAL en que el teléfono lo recibió, no con el `iat`
 * del servidor: con el reloj del teléfono adelantado, comparar contra `iat`
 * renovaría en cada vuelta. Sin hora guardada (token de antes de este
 * cambio) o con una hora absurda (reloj movido hacia atrás) se rota una vez
 * y queda guardada la hora correcta. */
export function refreshTokenNeedsRotation(
  receivedAtMs: number | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (typeof receivedAtMs !== "number" || !Number.isFinite(receivedAtMs)) return true;
  const ageMs = nowMs - receivedAtMs;
  return ageMs > DAY_MS || ageMs < -DAY_MS;
}

/** ¿Los dos tokens son de la misma cuenta? Si alguno no trae `sub`, no. */
export function sameTokenSubject(a: string | null | undefined, b: string | null | undefined): boolean {
  const subA = readTokenClaims(a)?.sub;
  const subB = readTokenClaims(b)?.sub;
  return Boolean(subA) && subA === subB;
}

export function isTokenExpired(token: string | null | undefined, nowMs: number = Date.now()): boolean {
  const claims = readTokenClaims(token);
  if (!claims || typeof claims.exp !== "number") return false;
  return claims.exp * 1000 <= nowMs;
}
